import "./form-person.scss";

import { LocalRole } from "@core/model/local-role.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { PersonFormSchema } from "@factory/form/form.rule";
import { useQyGetCardCategory } from "@factory/query/card-category.query";
import { useQyGetRole } from "@factory/query/role.query";
import { useQyGetTenant } from "@factory/query/tenant.query";
import { LabelValue } from "@shared/models/label-value.interface";
import CheckboxControl from "@shared/ui/hook-form/checkbox-control/checkbox-control";
import DropdownControl from "@shared/ui/hook-form/dropdown-control/dropdown-control";
import ImageControl from "@shared/ui/hook-form/image-control/image-control";
import InputControl from "@shared/ui/hook-form/input-control/input-control";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import InputOptControl from "@shared/ui/hook-form/input-opt-control/input-control";
import TextBlock from "@shared/ui/text-block/text-block";
import { Divider } from "primereact/divider";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import { appFeature } from "../../../app.feature";

type InputFormSchema = PersonFormSchema;
export interface FormPersonProps {
  isCustomer: boolean;
  hideCategory?: boolean;
}
export function FormPerson({ isCustomer, hideCategory }: FormPersonProps) {
  const customerPasscodeLength = appFeature.config.password.pinLength ?? 6;
  const { getUserRole } = useUserIdentityContext();
  const { control, watch } = useFormContext<PersonFormSchema>();
  const formRole = watch("role");
  const { data: tenantResponse } = useQyGetTenant("", 9999, 0);
  const tenantData = tenantResponse?.data || [];
  const tenantOptions = LabelValue.MapArray(
    tenantData,
    (item) => item.business_name,
    (item) => item.code
  );

  const { data: cardCategoryResponse } = useQyGetCardCategory("", 9999, 0);
  const cardCategoryData = cardCategoryResponse?.data || [];
  const cardCategoryOptions = LabelValue.MapArray(
    cardCategoryData,
    (item) => item?.name,
    (item) => item.code
  );

  const { data: roleResponse } = useQyGetRole("", 9999, 0);
  const roleData = roleResponse?.data || [];
  const mappedRole = roleData.map(
    (item) =>
      ({
        label: item.name,
        value: item.code,
      } as LabelValue)
  );
  const { name } = getUserRole();
  const roleName = name.toUpperCase();
  const selectedFormRole = mappedRole.find((item) => item.value === formRole);
  const availableRolesForManager = [
    LocalRole.Manager,
    LocalRole.CardOwner,
    LocalRole.Cashier,
  ];
  const filteredRole = useCallback(() => {
    switch (roleName) {
      case LocalRole.Admin:
        return mappedRole;

      default: {
        const availableRoles = mappedRole.filter((item) =>
          availableRolesForManager.includes(
            item.label.toUpperCase() as LocalRole
          )
        );
        return availableRoles;
      }
    }
  }, [roleName, mappedRole]);

  const roleElement = useCallback(() => {
    if (roleName === LocalRole.Admin || roleName === LocalRole.Manager) {
      return (
        <DropdownControl<InputFormSchema>
          control={control}
          name="role"
          label="Role"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter role"
          options={filteredRole()}
        />
      );
    } else {
      return (
        <TextBlock
          className="mb-10 block"
          label="Role"
          value={selectedFormRole?.label}
        />
      );
    }
  }, [roleName, filteredRole]);

  return (
    <div id="FormPerson" className="person">
      <ImageControl<InputFormSchema>
        control={control}
        name="photo"
        label="Person Photo"
        imagePreviewClass="w-32 h-32"
      />

      <Divider />

      <section className="block mt-10">
        {roleName.toUpperCase() === LocalRole.Admin ? (
          <DropdownControl<InputFormSchema>
            control={control}
            name="tenant"
            label="Tenant"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter tenant"
            options={tenantOptions}
          />
        ) : null}
        {!isCustomer ? roleElement() : null}
        <InputControl<InputFormSchema>
          control={control}
          name="firstName"
          label="First Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter firstName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="lastName"
          label="Last Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter lastName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="mobileNumber"
          label="Mobile Number"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter mobileNumber"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="email"
          label="Email"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter email"
        />
        <InputDateControl<InputFormSchema>
          control={control}
          name="birthday"
          label="Birthday"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="username"
          label="Username"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter username"
        />
        {isCustomer ? (
          <div>
            {!hideCategory ? (
              <DropdownControl<InputFormSchema>
                control={control}
                name="cardCategory"
                label="Card Category"
                className="w-full md:w-3/4"
                containerClassName="pb-2"
                options={cardCategoryOptions}
                placeholder="Enter criteria"
              />
            ) : null}
            <InputOptControl<InputFormSchema>
              control={control}
              name="password"
              label="Passcode"
              className="w-full md:w-3/4"
              containerClassName="pb-2"
              mask={true}
              integerOnly={true}
              length={customerPasscodeLength}
            />
          </div>
        ) : (
          <InputControl<InputFormSchema>
            control={control}
            name="password"
            label="Password"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter password"
            type="password"
          />
        )}
      </section>

      <section className="hidden">
        <CheckboxControl<InputFormSchema>
          control={control}
          name="isVerified"
          label="Is Verified"
          containerClassName="pb-2"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="avatar"
          label="Avatar"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter avatar"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="activationCode"
          label="Activation Code"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter activationCode"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="gender"
          label="Gender"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter gender"
        />
        <InputDateControl<InputFormSchema>
          control={control}
          name="birthday"
          label="Birthday"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
        />
        <CheckboxControl<InputFormSchema>
          control={control}
          name="isBlocked"
          label="Is Blocked"
          containerClassName="pb-2"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="fatherName"
          label="Father Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter fatherName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="motherName"
          label="Mother Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter motherName"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_1Number"
          label="Emergency Contact 1 Number"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_1Number"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_1Name"
          label="Emergency Contact 1 Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_1Name"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_1Relationship"
          label="Emergency Contact 1 Relationship"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_1Relationship"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_2Number"
          label="Emergency Contact 2 Number"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_2Number"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_2Name"
          label="Emergency Contact 2 Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_2Name"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="emergencyContact_2Relationship"
          label="Emergency Contact 2 Relationship"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter emergencyContact_2Relationship"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="sss"
          label="Sss"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter sss"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="philhealth"
          label="Philhealth"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter philhealth"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="pagibig"
          label="Pagibig"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter pagibig"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="tin"
          label="Tin"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter tin"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="medicalCondition"
          label="Medical Condition"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter medicalCondition"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="bloodType"
          label="Blood Type"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter bloodType"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="elementary"
          label="Elementary"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter elementary"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="highSchool"
          label="High School"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter highSchool"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="college"
          label="College"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter college"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="postCollege"
          label="Post College"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter postCollege"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="undergraduate"
          label="Undergraduate"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter undergraduate"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="curriculumVitae"
          label="Curriculum Vitae"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter curriculumVitae"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="supportingDoc"
          label="Supporting Doc"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter supportingDoc"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="intentLetter"
          label="Intent Letter"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter intentLetter"
        />
        <InputControl<InputFormSchema>
          control={control}
          name="applicationLetter"
          label="Application Letter"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter applicationLetter"
        />
      </section>
    </div>
  );
}

export default FormPerson;
